import React, { useEffect, useState } from "react";
import Navbar from "@/components/common/Navbar";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getViewsByEmail } from "@/services/views";
import { fetchAuthSession } from "aws-amplify/auth";
import amplifyOutputs from "@/amplify_outputs.json";
// import { jwt_decode } from "jwt-decode";

const Home = () => {
  const [views, setViews] = useState([]);
  const [selectedView, setSelectedView] = useState(null);
  const [dashboardUrl, setDashboardUrl] = useState("");
  const [isLoadingUrl, setIsLoadingUrl] = useState(false);
  const [urlError, setUrlError] = useState("");

  const QUICKSIGHT_CONFIG = {
    lambdaUrl: amplifyOutputs.custom?.quicksightFunctionUrl || "",
    // Add other config values as needed
  };

  const handleViewSelect = (viewId) => {
    const view = views.find((view) => view.id === viewId);
    setSelectedView(view);
    localStorage.setItem("selectedDashboardId", viewId); // persist
  };

  const fetchViews = async () => {
    try {
      const views = await getViewsByEmail();
      // Filter out views with empty or null names
      const validViews = views.filter((view) => view.name && view.name.trim() !== "");
      setViews(validViews);

      const savedId = localStorage.getItem("selectedDashboardId");
      const matchedView = validViews.find((v) => v.id === savedId);
      if (matchedView) {
        setSelectedView(matchedView);
      } else {
        const defaultView = validViews.find((view) => view.isDefault);
        setSelectedView(defaultView || validViews[0]);
      }
    } catch (error) {
      console.error("Error fetching views:", error);
    }
  };

  // Function to get QuickSight URL from Lambda
  const getQuickSightUrl = async () => {
    try {
      setIsLoadingUrl(true);
      setDashboardUrl("");
      setUrlError("");

      // Get the JWT token from AWS Amplify session
      const session = await fetchAuthSession();
      const jwtToken = session.tokens.accessToken.toString();

      // Call the Lambda function
      const response = await fetch(QUICKSIGHT_CONFIG.lambdaUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          "x-dashboard-id": selectedView?.quicksightDashboardId,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to get QuickSight URL: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();

      // Assuming the Lambda returns the URL in a 'url' field
      if (data.url) {
        setDashboardUrl(data.url);
      } else {
        throw new Error("No URL returned from Lambda function");
      }
    } catch (error) {
      console.error("Error getting QuickSight URL:", error);
      setUrlError(error.message);
    } finally {
      setIsLoadingUrl(false);
    }
  };

  // Effect to refresh token if accessToken becomes invalid
  useEffect(() => {
    if (views.length === 0) {
      fetchViews();
    }
  });

  // Effect to get QuickSight URL when component mounts
  useEffect(() => {
    if (selectedView?.quicksightDashboardId) {
      getQuickSightUrl();
    }
  }, [selectedView]);

  const QuickSightDashboard = () => {
    if (isLoadingUrl || !dashboardUrl) {
      return (
        <div className="flex items-center justify-center h-[600px] md:h-[700px] lg:h-[800px] xl:h-[900px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      );
    }

    if (urlError) {
      return (
        <div className="min-h-screen bg-gray-50 p-4 mt-4">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4">
              <h2 className="text-white text-xl font-semibold">
                Error Loading Dashboard
              </h2>
            </div>
            <div className="flex flex-col items-center justify-center h-96 space-y-4">
              <div className="text-red-600 text-lg">
                Failed to load dashboard
              </div>
              <div className="text-gray-600 text-sm">{urlError}</div>
              <button
                onClick={getQuickSightUrl}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gray-50 p-4 mt-4">
        {/* Header */}

        {/* Dashboard Container */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Dashboard Header */}

          {/* Loading Indicator */}
          <div className="relative">
            {/* Dashboard iframe */}
            <div className="relative bg-white">
              <iframe
                src={dashboardUrl}
                className="w-full h-[600px] md:h-[700px] lg:h-[800px] xl:h-[900px] border-0"
                frameBorder="0"
                allowFullScreen
                title="QuickSight Analytics Dashboard"
                onLoad={(e) => {
                  // Hide loading indicator when iframe loads
                  const loadingDiv =
                    e.target.parentElement.previousElementSibling;
                  if (loadingDiv) {
                    loadingDiv.style.display = "none";
                  }
                }}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm text-gray-600">
              <div className="flex items-center space-x-4">
                <span className="flex items-center"></span>
              </div>
              <div className="mt-2 sm:mt-0 flex items-center space-x-4">
                <button
                  onClick={getQuickSightUrl}
                  className="text-blue-600 hover:text-blue-800 transition-colors"
                  title="Refresh dashboard URL"
                >
                  Refresh
                </button>
                <span className="text-gray-500">Powered by AWS QuickSight</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex-1 flex-col md:flex">
      <Navbar />
      {views.length > 0 && (
        <div className="mx-8 mt-4 flex gap-2 items-center">
          <p className="font-medium">Select Dashboard</p>
          <Select
            onValueChange={handleViewSelect}
            value={selectedView?.id}
            defaultValue={selectedView?.id}
          >
            <SelectTrigger className="w-56">
              <SelectValue placeholder={selectedView?.name} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {views.map((view) => (
                  <SelectItem value={view.id} key={view.id}>
                    {view.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      )}

      <QuickSightDashboard />
    </div>
  );
};

export default Home;
